import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import KpiCard from '../dashboard/KpiCard';
import { describeChange } from '../dashboard/model';
import Block, { LINK_BUTTON, ROW_LINK } from '../home/Block';
import { checkpointSentence, goalProgress, quarterHeadline } from '../home/sentences';
import * as fmt from '../../lib/format';
import { TechData } from './types';
import { cameInSentence, comparedLabel, functionsSentence, monthSentence } from './sentences';

const CELL = 'px-3 py-2 text-right tabular-nums';

// 1. The month so far: three numbers, and the sentence that says them.
export function MonthBlock({ tech }: { tech: TechData }) {
  const n = tech.numbers;
  return (
    <Block title={`${fmt.month(tech.period)}${tech.period_over ? '' : ' so far'}`} className="lg:col-span-3">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard label="Shipped" value={fmt.count(n.shipped.value)} change={describeChange(n.shipped.change_pct)} comparedWith={n.shipped.change_pct === null ? undefined : comparedLabel(tech)} />
        <KpiCard label="In progress now" value={fmt.count(n.in_progress)} note="Right now" />
        <KpiCard label="Waiting" value={fmt.count(n.waiting)} note="Not moved for longer than usual" tip="home.waiting" />
      </div>
      <p className="mt-3 text-sm text-gray-600">{monthSentence(tech)}</p>
      <div className="mt-1 flex flex-wrap gap-x-3">
        <Link to="/flow?tab=waiting" className={ROW_LINK}>See waiting tickets</Link>
        <Link to="/plan?tab=intake" className={ROW_LINK}>See what came in</Link>
        <Link to="/report" className={ROW_LINK}>Write the report</Link>
      </div>
    </Block>
  );
}

// 2. The quarter's plan: how far the goals have come, the intake, and the projects furthest behind.
export function PlanBlock({ tech }: { tech: TechData }) {
  const q = tech.quarter;
  return (
    <Block title="Quarter plan" className="lg:col-span-3">
      {q ? (
        <>
          <p className="text-3xl font-semibold tracking-tight text-gray-900">{quarterHeadline(q.label, q.overall_pct)}</p>
          <p className="mt-1 text-sm text-gray-600">
            {fmt.counted(q.goals, 'goal')}. {checkpointSentence(q.checkpoint.reached, q.checkpoint.target_pct)} {cameInSentence(tech)}
          </p>
          {q.behind.length > 0 && (
            <>
              <p className="mt-4 text-sm font-medium text-gray-900">Furthest behind</p>
              <ul className="mt-1 divide-y divide-gray-100">
                {q.behind.map((p) => (
                  <li key={p.project_key} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5">
                    <p className="min-w-0 break-words text-sm text-gray-900">
                      {p.project_name ?? p.project_key} <span className="text-gray-500">({p.project_key})</span>
                    </p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-sm tabular-nums text-gray-700">{goalProgress(p.pct, p.tickets)}</span>
                      <Link to={`/plan?project_key=${encodeURIComponent(p.project_key)}`} className={ROW_LINK}>See the plan</Link>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-600">This quarter has no goal epics yet, so there is no progress to show.</p>
      )}
      <Link to="/plan" className={LINK_BUTTON}>
        Open the plan
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    </Block>
  );
}

// 3. The five functions side by side: who logged time, how much, and what they shipped and have open.
export function FunctionBlock({ tech }: { tech: TechData }) {
  return (
    <Block title="By function" className="lg:col-span-3">
      <p className="mb-3 text-sm text-gray-600">{functionsSentence(tech)}</p>
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[26rem] border-separate border-spacing-0 text-left text-sm">
          <caption className="sr-only">People, hours and tickets for each function this month</caption>
          <thead>
            <tr className="text-xs font-semibold uppercase tracking-wider text-gray-600">
              <th scope="col" className="sticky left-0 border-b border-gray-200 bg-white px-3 py-2">Function</th>
              <th scope="col" className="border-b border-gray-200 px-3 py-2 text-right">People</th>
              <th scope="col" className="border-b border-gray-200 px-3 py-2 text-right">Hours</th>
              <th scope="col" className="border-b border-gray-200 px-3 py-2 text-right">Shipped</th>
              <th scope="col" className="border-b border-gray-200 px-3 py-2 text-right">In progress</th>
            </tr>
          </thead>
          <tbody>
            {tech.functions
              .filter((f) => f.key !== 'Other' || f.shipped + f.in_progress > 0)
              .map((f) => (
                <tr key={f.key}>
                  <th scope="row" className="sticky left-0 border-b border-gray-100 bg-white px-3 py-2 text-left font-normal text-gray-900">{f.key === 'Other' ? 'Other (unassigned or elsewhere)' : f.key}</th>
                  <td className={`border-b border-gray-100 ${CELL} text-gray-700`}>{f.key === 'Other' ? <span aria-hidden>–</span> : fmt.count(f.people)}</td>
                  <td className={`border-b border-gray-100 ${CELL} text-gray-700`}>{f.key === 'Other' ? <span aria-hidden>–</span> : fmt.hours(f.hours)}</td>
                  <td className={`border-b border-gray-100 ${CELL} text-gray-900`}>{fmt.count(f.shipped)}</td>
                  <td className={`border-b border-gray-100 ${CELL} text-gray-900`}>{fmt.count(f.in_progress)}</td>
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr className="font-semibold text-gray-900">
              <th scope="row" className="sticky left-0 bg-white px-3 py-2 text-left">Tech</th>
              <td className={CELL}>{fmt.count(tech.totals.people)}</td>
              <td className={CELL}>{fmt.hours(tech.totals.hours)}</td>
              <td className={CELL}>{fmt.count(tech.totals.shipped)}</td>
              <td className={CELL}>{fmt.count(tech.totals.in_progress)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Link to="/plan?tab=function" className={LINK_BUTTON}>
        See who is on each goal
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
    </Block>
  );
}

// Behind "Show details": every project's month on one line.
export function ProjectsBlock({ tech }: { tech: TechData }) {
  return (
    <Block title="Projects" className="lg:col-span-3">
      <p className="mb-3 text-sm text-gray-600">
        {tech.projects.length < tech.projects_total
          ? `The ${fmt.count(tech.projects.length)} busiest of ${fmt.count(tech.projects_total)} projects.`
          : `${fmt.counted(tech.projects_total, 'project')} with work this month.`}
      </p>
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[34rem] border-separate border-spacing-0 text-left text-sm">
          <caption className="sr-only">Each project this month</caption>
          <thead>
            <tr className="text-xs font-semibold uppercase tracking-wider text-gray-600">
              <th scope="col" className="sticky left-0 border-b border-gray-200 bg-white px-3 py-2">Project</th>
              <th scope="col" className="border-b border-gray-200 px-3 py-2 text-right">Plan</th>
              <th scope="col" className="border-b border-gray-200 px-3 py-2 text-right">Shipped</th>
              <th scope="col" className="border-b border-gray-200 px-3 py-2 text-right">In progress</th>
              <th scope="col" className="border-b border-gray-200 px-3 py-2 text-right">Waiting</th>
              <th scope="col" className="border-b border-gray-200 px-3 py-2 text-right">Came in</th>
            </tr>
          </thead>
          <tbody>
            {tech.projects.map((p) => (
              <tr key={p.project_key}>
                <th scope="row" className="sticky left-0 border-b border-gray-100 bg-white px-3 py-2 text-left font-normal">
                  <Link to={`/plan?project_key=${encodeURIComponent(p.project_key)}`} className="break-words rounded text-gray-900 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                    {p.project_name ?? p.project_key}
                  </Link>
                </th>
                <td className={`border-b border-gray-100 ${CELL} text-gray-700`}>{p.pct === null ? <span aria-hidden>–</span> : fmt.percent(p.pct)}</td>
                <td className={`border-b border-gray-100 ${CELL} text-gray-900`}>{fmt.count(p.released)}</td>
                <td className={`border-b border-gray-100 ${CELL} text-gray-900`}>{fmt.count(p.in_progress)}</td>
                <td className={`border-b border-gray-100 ${CELL} text-gray-900`}>{fmt.count(p.waiting)}</td>
                <td className={`border-b border-gray-100 ${CELL} text-gray-900`}>{fmt.count(p.came_in)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Block>
  );
}
